/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Check, Flag, AlertCircle } from "lucide-react";
import logger from '../utils/logger';
import analytics from '../utils/analytics';


const TestTaking = ({ user, testId: propsTestId, onTestComplete }) => {
  const params = useParams();
  const navigate = useNavigate();

  // get the testId from the URL or props
  const testId = propsTestId || params.testId;

  // Shuffle array function for randomizing questions or answers
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const [testInfo, setTestInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(3600); // 1 hour in seconds
  const [showSummary, setShowSummary] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ randomizedAnswers, setRandomizedAnswers ] = useState({});
  const [ testStartTime, setTestStartTime] = useState(null);
  const [testCompleted, setTestCompleted] = useState(false);
  const [hasReachedLastQuestion, setHasReachedLastQuestion] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);

  // local storage key specific to this test and user
  const getStorageKey = () => `testProgress_${user.id}_${testId}`;

  // load progress from local storage
  const loadSavedProgress = () => {
    try {
      const savedData = localStorage.getItem(getStorageKey());
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        // check if the saved data is recent within 24 hours
        const savedTime = new Date(parsedData.timestamp || 0);
        const currentTime = new Date();
        const dayInMs = 24 * 60 * 60 * 1000;

        if (currentTime - savedTime < dayInMs) {
          return parsedData.data;
        }
      }
    } catch (error) {
      logger.error('failed_to_load_test_progress', {
        error: error.message,
        userId: user.id,
        testId,
        timestamp: new Date().toISOString()
      });
    }
    return null;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };


// Fetch Test Info
  useEffect(() => {
    const fetchTestInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tests/${testId}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        if (!response.ok) throw new Error('Failed to fetch test information');
        const data = await response.json();
        console.log('Fetched test info:', data);
        setTestInfo(data);
        setTimeRemaining(data.time_limit * 60); // Convert minutes to seconds

        // set test start time when test info is loaded
        if (!testStartTime) {
          setTestStartTime(new Date().toISOString());
          // Track test start event
          analytics.trackTestStart(testId, data.name, user.id, data.time_limit);
        }
        
        logger.info('test_info_fetched', {
          userId: user.id,
          testId,
          testName: data.name,
          timeLimit: data.time_limit,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error fetching test info:', error);
        setError(error.message);

        logger.error('failed_to_fetch_test_info', {
          error: error.message,
          userId: user.id,
          testId,
          timestamp: new Date().toISOString()
        });
      }
    };

    fetchTestInfo();
  }, [testId, user.id, testStartTime]);


// Fetch Questions
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        // Add cache-busting to ensure fresh questions
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tests/${testId}/questions`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch test questions');
        }
        const data = await response.json();
        
        // Debug: Log the raw data to see if image_url is present
        console.log('DEBUG: Raw question data:', data);
        data.forEach((q, i) => {
          console.log(`DEBUG: Question ${i + 1} image_url:`, q.image_url);
        });
        
        // Filter active questions and process them
        const activeQuestions = data
          .filter(question => question.is_active !== false)
          .map(question => ({
            ...question,
            answers: Array.isArray(question.answers) ? question.answers : JSON.parse(question.answers)
          }));

        let finalQuestions;
        let finalAnswersMap;
        
        // Check if we're resuming a test with saved progress
        if (window.testResumeData) {
          // Merge fresh question data with saved order/randomization
          const savedQuestions = window.testResumeData.savedQuestions;
          const savedRandomization = window.testResumeData.randomizedAnswers;
          
          // Create a map of fresh questions by ID for easy lookup
          const freshQuestionsById = {};
          activeQuestions.forEach(q => {
            freshQuestionsById[q.id] = q;
          });
          
          // Rebuild questions array preserving saved order but with fresh content
          finalQuestions = savedQuestions.map(savedQ => {
            const freshQ = freshQuestionsById[savedQ.id];
            if (freshQ) {
              // Use fresh question data (including any new images) but preserve structure
              return {
                ...freshQ,
                // Keep any order-critical properties from saved data if needed
              };
            }
            return savedQ; // Fallback to saved question if not found
          });
          
          finalAnswersMap = savedRandomization;
          
          // Clear the resume data
          delete window.testResumeData;
        } else {
          // Normal flow - randomize everything fresh
          finalQuestions = activeQuestions;

          // Create randomized answer orders for each question
          const answersMap = {};
          finalQuestions.forEach(question => {
            // Only randomize multiple choice questions that have answers array (exclude true/false)
            if (question.answers && Array.isArray(question.answers) && question.answers.length > 1 && question.question_type !== 'true_false') {
              // Create a mapping of shuffled indices to original indices
              const indices = question.answers.map((_, index) => index);
              const shuffledIndices = shuffleArray([...indices]);
              answersMap[question.id] = shuffledIndices;
            }
          });
          
          finalAnswersMap = answersMap;
        }
        
        // Debug: Log the final processed questions
        console.log('DEBUG: Final processed questions:', finalQuestions);
        finalQuestions.forEach((q, i) => {
          console.log(`DEBUG: Final question ${i + 1} image_url:`, q.image_url);
        });
        
        setRandomizedAnswers(finalAnswersMap);
        setQuestions(finalQuestions);
        setIsLoading(false);

        logger.info('questions_fetched', {
          userId: user.id,
          testId,
          questionCount: finalQuestions.length,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error fetching questions:', error);
        setError(error.message);
        setIsLoading(false);

        logger.error('questions_fetch_failed', {
          error: error.message,
          userId: user.id,
          testId,
          timestamp: new Date().toISOString()
        });
      }
    };

    if (testInfo) {
      fetchQuestions();
    }
  }, [testInfo, testId, user.id]);

  // Update progress bar
  useEffect(() => {
    if (questions.length > 0) {
      setProgress((currentQuestionIndex / questions.length) * 100);
    }
  }, [currentQuestionIndex, questions.length]);


// Handle Answer Selection
  const handleAnswerSelect = (answerId) => {
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestionIndex].id]: answerId
    }));

    // Show saved indicator
    setShowSavedIndicator(true);
    setTimeout(() => setShowSavedIndicator(false), 2000);
  };

  // Handle Written Answer
  const handleWrittenAnswer = (text) => {
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestionIndex].id]: text
    }));

    // Show saved indicator
    setShowSavedIndicator(true);
    setTimeout(() => setShowSavedIndicator(false), 2000);
  };

  // Handle Next Question Navigation
  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
    } else {
      setShowSummary(true);
    }
  };

  // Handle navigate to previous question
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prevIndex => prevIndex - 1);
    }
  };

  // Handle Flag Question
  const handleFlagQuestion = () => {
    const questionId = questions[currentQuestionIndex].id;

    if (flaggedQuestions.includes(questionId)) {
      setFlaggedQuestions(prev => prev.filter(id => id !== questionId));
    } else {
      setFlaggedQuestions(prev => [...prev, questionId]);
    }

    logger.info('question_flagged', {
      userId: user.id,
      testId,
      questionId,
      flagged: flaggedQuestions.includes(questionId),
      timestamp: new Date().toISOString()
    });
  };


  // Handle navigate to specific question
  const handleGoToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setShowSummary(false);
  };

  // Submit the test
  const handleTestSubmit = async () => {
    // Pre-submit validation
    const unansweredCount = questions.length - Object.keys(answers).length;

    if (unansweredCount > 0) {
      const confirmSubmit = window.confirm(
        `⚠️ Warning: You have ${unansweredCount} unanswered question${unansweredCount > 1 ? 's' : ''}.\n\nUnanswered questions will be marked as incorrect. Are you sure you want to submit?`
      );

      if (!confirmSubmit) {
        return; // Don't submit if user cancels
      }
    }

    try {
      logger.info('submitting_test', {
        userId: user.id,
        testId,
        answeredQuestions: Object.keys(answers).length,
        totalQuestions: questions.length,
        unansweredCount,
        timestamp: new Date().toISOString()
      });
      
      // Calculate time spent
      const endTime = new Date();
      const startTime = new Date(testStartTime);
      const timeSpent = Math.floor((endTime - startTime) / 1000);
      

    // Calculate score (number of correct answers)
    const score = questions.reduce((correctCount, question) => {
      const userAnswer = answers[question.id];
      const correctAnswer = question.correct_answer;
      
      // Handle different question types
      if (userAnswer !== undefined && correctAnswer !== undefined) {
        // For written answers, compare directly
        if (typeof userAnswer === 'string' && !Number.isInteger(Number(userAnswer))) {
          // For written questions, we can't easily validate on frontend, count as attempted
          // Server will do the actual validation
          return correctCount + 1; // Optimistic counting for progress display
        } else {
          // For multiple choice, get the actual answer text the user selected
          const actualIndex = randomizedAnswers[question.id] 
            ? randomizedAnswers[question.id][userAnswer] 
            : userAnswer;
          
          const selectedAnswerText = question.answers[actualIndex];
          if (selectedAnswerText === correctAnswer) {
            return correctCount + 1;
          }
        }
      }
      return correctCount;
    }, 0);
    
    // Map answers to match the server's expected format
    const formattedAnswers = {};
    
    Object.entries(answers).forEach(([questionId, answerIndex]) => {
      // Get the actual answer text from the question's answers array
      const question = questions.find(q => q.id.toString() === questionId.toString());
      if (question) {
        // Handle written answers (text responses)
        if (typeof answerIndex === 'string' && !Number.isInteger(Number(answerIndex))) {
          formattedAnswers[questionId] = answerIndex;
        } else {
          // Handle multiple choice answers with potential randomization
          const actualIndex = randomizedAnswers[questionId] 
            ? randomizedAnswers[questionId][answerIndex] 
            : answerIndex;
          
          if (question.answers[actualIndex]) {
            formattedAnswers[questionId] = question.answers[actualIndex];
          }
        }
      }
    });
    
    // Prepare submission data
    const submissionData = {
      userId: user.id,
      answers: formattedAnswers,
      score,
      timeSpent
    };
    
    // Submit the test results to the correct endpoint
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tests/${testId}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(submissionData),
      credentials: 'include'
    });
    
    if (!response.ok) throw new Error('Failed to submit test');
    
    const result = await response.json();
    
    // Calculate time spent
    const testEndTime = new Date();
    const testStart = new Date(testStartTime);
    const timeSpentMs = testEndTime - testStart;
    
    // Track test completion
    analytics.trackTestComplete(
      testId, 
      testInfo.name, 
      result.score, 
      questions.length, 
      result.percentage, 
      result.passed,
      timeSpentMs
    );
    
    // Mark test as completed to prevent abandonment tracking
    setTestCompleted(true);
    
    // Clear saved progress after successful submission
    localStorage.removeItem(getStorageKey());
    
    logger.info('test_submitted', {
      userId: user.id,
      testId,
      score,
      percentage: result.percentage,
      passed: result.passed,
      timestamp: new Date().toISOString()
    });
    
    // Call the parent component callback with the results
    onTestComplete(
      score,
      questions.length,
      result.percentage,
      result.passed,
      testInfo.pass_threshold || 90
    );
  } catch (error) {
    logger.error('test_submission_failed', {
      error: error.message,
      userId: user.id,
      testId,
      timestamp: new Date().toISOString()
    });
    setError('Failed to submit test. Please try again.');
  }
};
  

  // Check for existing test progress on component mount
  useEffect(() => {
    const savedProgress = loadSavedProgress();
    
    if (savedProgress) {
      // Ask user if they want to resume
      const wantToResume = window.confirm(
        `You have an unfinished test from ${new Date(savedProgress.timestamp).toLocaleString()}. Would you like to resume where you left off?`
      );
      
      if (wantToResume) {
        // Restore progress data but fetch fresh questions to get any updates
        setTestInfo(savedProgress.testInfo);
        setCurrentQuestionIndex(savedProgress.currentQuestionIndex);
        setAnswers(savedProgress.answers);
        setRandomizedAnswers(savedProgress.randomizedAnswers || {});
        setFlaggedQuestions(savedProgress.flaggedQuestions || []);
        setTimeRemaining(savedProgress.timeRemaining);
        setTestStartTime(savedProgress.testStartTime);
        setShowSummary(savedProgress.showSummary || false);
        setHasReachedLastQuestion(savedProgress.hasReachedLastQuestion || false);
        
        // Set a flag to indicate we're resuming with saved order
        window.testResumeData = {
          savedQuestions: savedProgress.questions,
          randomizedAnswers: savedProgress.randomizedAnswers || {}
        };
        
        // Let the normal fetch flow get fresh questions from server
        // but preserve the original order and randomization
        return;
      } else {
        // User chose not to resume, clear saved progress
        localStorage.removeItem(getStorageKey());
      }
    }
  }, [testId, user.id]);
    

  // save progress on key state changes
  useEffect(() => {
    if(!testInfo || !questions.length) return;

    const progressData = {
      testInfo,
      questions,
      currentQuestionIndex,
      answers,
      randomizedAnswers,
      flaggedQuestions,
      timeRemaining,
      testStartTime,
      showSummary,
      hasReachedLastQuestion,
      timestamp: new Date().toISOString()
    };

    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(progressData));

      logger.info('test_progress_saved', {
        userId: user.id,
        testId,
        questionIndex: currentQuestionIndex,
        questionsAnswered: Object.keys(answers).length,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      logger.error('failed_to_save_test_progress', {
        error: error.message,
        userId: user.id,
        testId,
        timestamp: new Date().toISOString()
      });
    }
  }, [testInfo, questions, currentQuestionIndex, answers, randomizedAnswers, flaggedQuestions, timeRemaining, testStartTime, showSummary, hasReachedLastQuestion]);

  // Track when user reaches the last question
  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex === questions.length - 1) {
      setHasReachedLastQuestion(true);
    }
  }, [currentQuestionIndex, questions.length]);

  // Track test abandonment on component unmount
  useEffect(() => {
    return () => {
      // Only track abandonment if test was started but not completed
      if (testStartTime && !testCompleted && testInfo) {
        const testEndTime = new Date();
        const testStart = new Date(testStartTime);
        const timeSpentMs = testEndTime - testStart;
        
        analytics.trackTestAbandon(
          testId,
          testInfo.name,
          Object.keys(answers).length,
          timeSpentMs
        );
      }
    };
  }, [testStartTime, testCompleted, testInfo, testId, answers]);

    // Handle timer
  useEffect(() => {
    if (!testInfo || timeRemaining === null) return;

    const timerInterval = setInterval(() => {
      setTimeRemaining(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timerInterval);
          setShowSummary(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [testInfo, timeRemaining]);

  // 4. Fix the beforeunload event handler:
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (testInfo && questions.length && !isLoading) {
        const message = 'You have an unfinished test. Your progress has been saved, but if you leave you will need to resume later.';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [testInfo, questions, isLoading]);


  if (isLoading) {
    return <div className="text-center p-8">Loading test...</div>;
  }
  
  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <Button onClick={() => navigate('/available-tests')}>Return to Tests</Button>
      </div>
    );
  }
  
  if (!testInfo || !questions.length) {
    return <div className="text-center p-8">No test data available.</div>;
  }


// if showing summary page
  if (showSummary) {
    return (
      <div className="container mx-auto p-2 sm:p-4 max-w-4xl">
        <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
          <h1 className="text-xl sm:text-2xl font-bold mb-6">Test Summary</h1>
          
          <div className="mb-4">
            <p className="text-gray-600">Please review your answers before submitting the test:</p>
            <p className="text-sm text-gray-500 mt-2">
              <span className="inline-block w-3 h-3 bg-yellow-100 border border-yellow-400 mr-1"></span> Flagged questions
              <span className="inline-block w-3 h-3 bg-red-100 border border-red-400 ml-4 mr-1"></span> Unanswered questions
            </p>
          </div>
          
          <div className="divide-y">
            {questions.map((question, index) => {
              const isAnswered = answers[question.id] !== undefined;
              const isFlagged = flaggedQuestions.includes(question.id);
              
              // Get the selected answer text if answered
              let answerText = "Not answered";
              if (isAnswered) {
                const questionType = question.question_type || 'multiple_choice';
                const answer = answers[question.id];
                
                if (questionType === 'written') {
                  answerText = answer || "No written response";
                } else {
                  // For multiple choice, true/false, and image-based questions
                  const answerIndex = answer;
                  if (typeof answerIndex === 'number') {
                    // Get the display answers for this question (handles randomization)
                    const questionAnswers = question.answers;
                    const displayAnswers = randomizedAnswers[question.id]
                      ? randomizedAnswers[question.id].map(index => questionAnswers[index])
                      : questionAnswers;
                    
                    if (displayAnswers[answerIndex]) {
                      answerText = displayAnswers[answerIndex];
                    }
                  }
                }
              }
              
              return (
                <div 
                  key={question.id} 
                  className={`py-4 ${
                    isFlagged 
                      ? 'bg-yellow-50 border-l-4 border-yellow-400 pl-4' 
                      : !isAnswered 
                        ? 'bg-red-50 border-l-4 border-red-400 pl-4' 
                        : ''
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold">
                        Question {index + 1}
                        {isFlagged && <span className="text-yellow-600 ml-2">⚠ Flagged</span>}
                      </h3>
                      <p className="mt-1 break-words">{question.question_text}</p>
                      
                      {/* Display question image in summary if it exists */}
                      {question.image_url && (
                        <div className="mt-3 text-center">
                          <img 
                            src={question.image_url.startsWith('http') ? question.image_url : `${process.env.REACT_APP_API_URL}${question.image_url}`}
                            alt="Question image" 
                            className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                            style={{ maxHeight: '250px' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      <div className="mt-2">
                        {isAnswered ? (
                          <div>
                            <p className="font-medium text-green-700">Your answer:</p>
                            {question.question_type === 'written' ? (
                              <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                                {answerText.length > 100 
                                  ? `${answerText.substring(0, 100)}...` 
                                  : answerText
                                }
                              </div>
                            ) : (
                              <p className="text-gray-700">{answerText}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-red-500 font-medium">Not answered</span>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleGoToQuestion(index)}
                      className="min-h-[44px] flex-shrink-0 self-start sm:self-auto"
                    >
                      Review Question
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowSummary(false)}
              className="min-h-[44px] w-full sm:w-auto"
            >
              Return to Test
            </Button>

            <Button
              onClick={handleTestSubmit}
              className="bg-green-600 hover:bg-green-700 text-white min-h-[44px] w-full sm:w-auto"
            >
              Confirm and Submit
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Test taking view
const currentQuestion = questions[currentQuestionIndex];
  
  // Debug: Log current question details
  if (currentQuestion) {
    console.log('DEBUG: Current question object:', currentQuestion);
    console.log('DEBUG: Current question image_url:', currentQuestion.image_url);
    console.log('DEBUG: Image_url type:', typeof currentQuestion.image_url);
    console.log('DEBUG: Image_url truthiness:', !!currentQuestion.image_url);
  }
  
  // Get the answer options for the current question
  const answerOptions = currentQuestion ? currentQuestion.answers : [];
  
  // If the answers are randomized, we need to reorder them for display
  const displayAnswers = currentQuestion && randomizedAnswers[currentQuestion.id]
    ? randomizedAnswers[currentQuestion.id].map(index => answerOptions[index])
    : answerOptions;
  
  const selectedAnswer = answers[currentQuestion?.id];
  const isFlagged = flaggedQuestions.includes(currentQuestion?.id);
  
  // Render different question types
  const renderQuestionContent = () => {
    if (!currentQuestion) return null;
    
    const questionType = currentQuestion.question_type || 'multiple_choice';
    
    switch (questionType) {
      case 'true_false':
        return (
          <div className="space-y-4">
            {currentQuestion.image_url && (
              <div className="text-center">
                {console.log('DEBUG: Rendering image for question:', currentQuestion.id, 'URL:', currentQuestion.image_url)}
                <img 
                  src={currentQuestion.image_url.startsWith('http') ? currentQuestion.image_url : `${process.env.REACT_APP_API_URL}${currentQuestion.image_url}`}
                  alt="Question image" 
                  className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                  style={{ maxHeight: '400px' }}
                  onLoad={() => {
                    console.log('DEBUG: Image loaded successfully:', currentQuestion.image_url);
                  }}
                  onError={(e) => {
                    console.error('DEBUG: Failed to load image:', currentQuestion.image_url);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            {!currentQuestion.image_url && console.log('DEBUG: No image for question:', currentQuestion.id)}
            <div className="space-y-3">
            {currentQuestion.answers.map((option, index) => (
              <div
                key={index}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedAnswer === index
                    ? 'bg-blue-100 border-blue-500 text-blue-700'
                    : 'hover:bg-gray-50 border-gray-200'
                }`}
                onClick={() => handleAnswerSelect(index)}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedAnswer === index
                      ? 'bg-blue-500 border-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedAnswer === index && (
                      <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                    )}
                  </div>
                  <span className="text-lg">{option}</span>
                </div>
              </div>
            ))}
            </div>
          </div>
        );
        
      case 'written':
        return (
          <div className="space-y-4">
            {currentQuestion.image_url && (
              <div className="text-center">
                {console.log('DEBUG: Rendering image for question:', currentQuestion.id, 'URL:', currentQuestion.image_url)}
                <img 
                  src={currentQuestion.image_url.startsWith('http') ? currentQuestion.image_url : `${process.env.REACT_APP_API_URL}${currentQuestion.image_url}`}
                  alt="Question image" 
                  className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                  style={{ maxHeight: '400px' }}
                  onLoad={() => {
                    console.log('DEBUG: Image loaded successfully:', currentQuestion.image_url);
                  }}
                  onError={(e) => {
                    console.error('DEBUG: Failed to load image:', currentQuestion.image_url);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            {!currentQuestion.image_url && console.log('DEBUG: No image for question:', currentQuestion.id)}
            <div className="space-y-3">
            <Label htmlFor="written-answer" className="text-base font-medium">
              Your Answer:
            </Label>
            <Textarea
              id="written-answer"
              value={selectedAnswer || ''}
              onChange={(e) => handleWrittenAnswer(e.target.value)}
              placeholder="Type your answer here..."
              className="min-h-[120px] resize-none"
            />
            <p className="text-sm text-gray-500">
              Take your time to provide a complete answer.
            </p>
            </div>
          </div>
        );
        
      case 'image_based':
        return (
          <div className="space-y-4">
            {currentQuestion.image_url && (
              <div className="text-center">
                {console.log('DEBUG: Rendering image for question:', currentQuestion.id, 'URL:', currentQuestion.image_url)}
                <img 
                  src={currentQuestion.image_url.startsWith('http') ? currentQuestion.image_url : `${process.env.REACT_APP_API_URL}${currentQuestion.image_url}`}
                  alt="Question image" 
                  className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                  style={{ maxHeight: '400px' }}
                  onLoad={() => {
                    console.log('DEBUG: Image loaded successfully:', currentQuestion.image_url);
                  }}
                  onError={(e) => {
                    console.error('DEBUG: Failed to load image:', currentQuestion.image_url);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            {!currentQuestion.image_url && console.log('DEBUG: No image for question:', currentQuestion.id)}
            <div className="space-y-2">
              {displayAnswers.map((option, index) => (
                <div 
                  key={index}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedAnswer === index 
                      ? 'bg-blue-100 border-blue-500 text-blue-700' 
                      : 'hover:bg-gray-50 border-gray-200'
                  }`}
                  onClick={() => handleAnswerSelect(index)}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      selectedAnswer === index 
                        ? 'bg-blue-500 border-blue-500' 
                        : 'border-gray-300'
                    }`}>
                      {selectedAnswer === index && (
                        <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                      )}
                    </div>
                    <span>{option}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        
      case 'multiple_choice':
      default:
        return (
          <div className="space-y-4">
            {currentQuestion.image_url && (
              <div className="text-center">
                {console.log('DEBUG: Rendering image for question:', currentQuestion.id, 'URL:', currentQuestion.image_url)}
                <img 
                  src={currentQuestion.image_url.startsWith('http') ? currentQuestion.image_url : `${process.env.REACT_APP_API_URL}${currentQuestion.image_url}`}
                  alt="Question image" 
                  className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                  style={{ maxHeight: '400px' }}
                  onLoad={() => {
                    console.log('DEBUG: Image loaded successfully:', currentQuestion.image_url);
                  }}
                  onError={(e) => {
                    console.error('DEBUG: Failed to load image:', currentQuestion.image_url);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            {!currentQuestion.image_url && console.log('DEBUG: No image for question:', currentQuestion.id)}
            <div className="space-y-2">
              {displayAnswers.map((option, index) => (
              <div 
                key={index}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedAnswer === index 
                    ? 'bg-blue-100 border-blue-500 text-blue-700' 
                    : 'hover:bg-gray-50 border-gray-200'
                }`}
                onClick={() => handleAnswerSelect(index)}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedAnswer === index 
                      ? 'bg-blue-500 border-blue-500' 
                      : 'border-gray-300'
                  }`}>
                    {selectedAnswer === index && (
                      <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                    )}
                  </div>
                  <span>{option}</span>
                </div>
              </div>
            ))}
            </div>
          </div>
        );
    }
  };

  if (isLoading) return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-8 text-center">
        <p className="text-lg">Loading test questions...</p>
      </CardContent>
    </Card>
  );

  if (error) return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-8 text-center">
        <p className="text-red-600">Error: {error}</p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Try Again
        </Button>
      </CardContent>
    </Card>
  );

  if (questions.length === 0) return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-8 text-center">
        <p className="text-lg">No questions available for this test.</p>
        <Button onClick={() => window.history.back()} className="mt-4">
          Go Back
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-2 sm:p-4 max-w-4xl">
      <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
        <div className="sticky top-0 z-20 bg-white -mx-4 sm:-mx-6 px-4 sm:px-6 pt-1 pb-3 mb-4 border-b flex justify-between items-center gap-3">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold truncate">{testInfo.name}</h1>
            <div className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">
              Required to pass: <span className="font-semibold text-blue-600">{testInfo.pass_threshold || 90}%</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-xs sm:text-sm text-gray-500 mb-0.5 sm:mb-1">Time Remaining</div>
            <div className="text-lg sm:text-xl font-bold tabular-nums">{formatTime(timeRemaining)}</div>
          </div>
        </div>

        {/* Counter for Unanswered/Flagged Questions */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-gray-700">
                <span className="font-semibold text-amber-600">
                  {questions.length - Object.keys(answers).length}
                </span> Unanswered
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Flag className="h-4 w-4 text-yellow-600" />
              <span className="text-gray-700">
                <span className="font-semibold text-yellow-600">
                  {flaggedQuestions.length}
                </span> Flagged
              </span>
            </div>
          </div>
          {showSavedIndicator && (
            <div className="flex items-center gap-2 text-green-600 animate-fade-in">
              <Check className="h-4 w-4" />
              <span className="font-medium">Saved</span>
            </div>
          )}
        </div>

        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-500 mb-1">
            <span>Question {currentQuestionIndex + 1} of {questions.length} — {progress.toFixed(0)}%</span>
            <span>{Object.keys(answers).length}/{questions.length} Answered</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
        
        <div className={`mb-8 ${isFlagged ? 'border-l-4 border-yellow-400 pl-4' : ''}`}>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
            <h2 className="text-lg sm:text-xl font-semibold break-words">{currentQuestion?.question_text}</h2>

            <Button
              variant={isFlagged ? "default" : "outline"}
              size="sm"
              className={`min-h-[44px] flex-shrink-0 self-start sm:self-auto ${isFlagged ? "bg-yellow-500 hover:bg-yellow-600" : ""}`}
              onClick={handleFlagQuestion}
            >
              {isFlagged ? "Unflag Question" : "Flag for Review"}
            </Button>
          </div>
          
          {renderQuestionContent()}
        </div>
        
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex gap-2">
            <Button
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0}
              variant="outline"
              className="min-h-[44px]"
            >
              Previous
            </Button>

            {currentQuestionIndex < questions.length - 1 ? (
              <Button
                onClick={handleNextQuestion}
                className="min-h-[44px]"
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={() => setShowSummary(true)}
                className="bg-green-600 hover:bg-green-700 min-h-[44px]"
              >
                Review All Answers
              </Button>
            )}
          </div>

          {(currentQuestionIndex < questions.length - 1 && hasReachedLastQuestion) && (
            <Button
              variant="outline"
              onClick={() => setShowSummary(true)}
              className="min-h-[44px]"
            >
              Show Summary
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestTaking;